/**
 * WebhookAdmin.js
 * Admin-Übersicht für Webhook Queue und Dead Letter Queue
 */

import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import {
  RefreshCw,
  Send,
  AlertTriangle,
  CheckCircle,
  Clock,
  XCircle,
  RotateCcw,
  Filter,
  Play
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const WebhookAdmin = () => {
  const [webhooks, setWebhooks] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState(null);
  const [retrying, setRetrying] = useState({});
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    loadWebhooks();
  }, [filter]);

  const loadWebhooks = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const url = filter 
        ? `${API_URL}/api/admin/webhooks?status=${filter}&limit=100`
        : `${API_URL}/api/admin/webhooks?limit=100`;
      
      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      
      setWebhooks(data.webhooks || []);
      setStats(data.stats || {});
    } catch (error) {
      toast.error('Fehler beim Laden der Webhooks');
    } finally {
      setLoading(false);
    }
  };

  const handleRetry = async (eventId) => {
    setRetrying(prev => ({ ...prev, [eventId]: true }));
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/admin/webhooks/${eventId}/retry`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      
      if (data.success) {
        toast.success('Webhook erfolgreich gesendet!');
      } else {
        toast.error(data.message || 'Retry fehlgeschlagen');
      }
      
      loadWebhooks();
    } catch (error) {
      toast.error('Fehler beim Retry');
    } finally {
      setRetrying(prev => ({ ...prev, [eventId]: false }));
    }
  };

  const handleProcessQueue = async () => {
    setProcessing(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/admin/webhooks/process-queue`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      
      toast.success(`${data.processed} Webhooks verarbeitet`);
      loadWebhooks();
    } catch (error) {
      toast.error('Fehler beim Queue-Processing');
    } finally {
      setProcessing(false);
    }
  };

  const getStatusBadge = (status) => {
    const config = {
      pending: { color: 'bg-yellow-500', icon: Clock, label: 'Pending' },
      sent: { color: 'bg-green-500', icon: CheckCircle, label: 'Gesendet' },
      retrying: { color: 'bg-blue-500', icon: RotateCcw, label: 'Retry' },
      failed: { color: 'bg-red-500', icon: XCircle, label: 'Fehlgeschlagen' }
    };
    
    const c = config[status] || config.pending;
    const Icon = c.icon;
    
    return (
      <Badge className={`${c.color} text-white`}>
        <Icon className="h-3 w-3 mr-1" />
        {c.label}
      </Badge>
    );
  };

  const formatDate = (iso) => {
    if (!iso) return '-';
    return new Date(iso).toLocaleString('de-DE');
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Webhook Queue</h1>
          <p className="text-zinc-400">Übersicht aller ausgehenden Webhooks</p>
        </div>
        <div className="flex gap-2">
          <Button 
            onClick={handleProcessQueue}
            disabled={processing}
            variant="outline"
          >
            {processing ? (
              <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Play className="h-4 w-4 mr-2" />
            )}
            Queue verarbeiten
          </Button>
          <Button onClick={loadWebhooks} variant="outline">
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Aktualisieren
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-5 gap-4">
        <Card className="bg-zinc-900 border-zinc-800 cursor-pointer hover:border-zinc-700"
              onClick={() => setFilter(null)}>
          <CardContent className="p-4 text-center">
            <p className="text-3xl font-bold text-white">{stats.total || 0}</p>
            <p className="text-zinc-400 text-sm">Gesamt</p>
          </CardContent>
        </Card>
        <Card className="bg-zinc-900 border-zinc-800 cursor-pointer hover:border-yellow-600"
              onClick={() => setFilter('pending')}>
          <CardContent className="p-4 text-center">
            <p className="text-3xl font-bold text-yellow-500">{stats.pending || 0}</p>
            <p className="text-zinc-400 text-sm">Pending</p>
          </CardContent>
        </Card>
        <Card className="bg-zinc-900 border-zinc-800 cursor-pointer hover:border-green-600"
              onClick={() => setFilter('sent')}>
          <CardContent className="p-4 text-center">
            <p className="text-3xl font-bold text-green-500">{stats.sent || 0}</p>
            <p className="text-zinc-400 text-sm">Gesendet</p>
          </CardContent>
        </Card>
        <Card className="bg-zinc-900 border-zinc-800 cursor-pointer hover:border-blue-600"
              onClick={() => setFilter('retrying')}>
          <CardContent className="p-4 text-center">
            <p className="text-3xl font-bold text-blue-500">{stats.retrying || 0}</p>
            <p className="text-zinc-400 text-sm">Retry</p>
          </CardContent>
        </Card>
        <Card className="bg-zinc-900 border-zinc-800 cursor-pointer hover:border-red-600"
              onClick={() => setFilter('failed')}>
          <CardContent className="p-4 text-center">
            <p className="text-3xl font-bold text-red-500">{stats.failed || 0}</p>
            <p className="text-zinc-400 text-sm">Fehlgeschlagen</p>
          </CardContent>
        </Card>
      </div>

      {/* Filter Anzeige */}
      {filter && (
        <div className="flex items-center gap-2">
          <Filter className="h-4 w-4 text-zinc-400" />
          <span className="text-zinc-400">Filter:</span>
          <Badge variant="outline">{filter}</Badge>
          <Button variant="ghost" size="sm" onClick={() => setFilter(null)}>
            <XCircle className="h-4 w-4" />
          </Button>
        </div>
      )}

      {/* Webhook Liste */}
      <Card className="bg-zinc-900 border-zinc-800">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <Send className="h-5 w-5" />
            Webhooks ({webhooks.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8 text-zinc-400">
              <RefreshCw className="h-8 w-8 animate-spin mx-auto mb-2" />
              Lade Webhooks...
            </div>
          ) : webhooks.length === 0 ? (
            <div className="text-center py-8 text-zinc-400">
              Keine Webhooks gefunden
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-zinc-800 text-zinc-400 text-sm">
                    <th className="text-left py-3 px-2">Event ID</th>
                    <th className="text-left py-3 px-2">Typ</th>
                    <th className="text-left py-3 px-2">Status</th>
                    <th className="text-left py-3 px-2">Versuche</th>
                    <th className="text-left py-3 px-2">Erstellt</th>
                    <th className="text-left py-3 px-2">Letzter Fehler</th>
                    <th className="text-left py-3 px-2">Aktion</th>
                  </tr>
                </thead>
                <tbody>
                  {webhooks.map((webhook) => (
                    <tr key={webhook.event_id} className="border-b border-zinc-800 hover:bg-zinc-800/50">
                      <td className="py-3 px-2">
                        <code className="text-xs text-zinc-400 bg-zinc-800 px-2 py-1 rounded">
                          {webhook.event_id?.substring(0, 25)}...
                        </code>
                      </td>
                      <td className="py-3 px-2">
                        <Badge variant="outline" className="text-primary">
                          {webhook.event}
                        </Badge>
                      </td>
                      <td className="py-3 px-2">
                        {getStatusBadge(webhook.status)}
                      </td>
                      <td className="py-3 px-2 text-zinc-400">
                        {webhook.attempt_count || 0} / {webhook.max_attempts || 7}
                      </td>
                      <td className="py-3 px-2 text-zinc-400 text-sm">
                        {formatDate(webhook.created_at)}
                      </td>
                      <td className="py-3 px-2">
                        {webhook.last_error ? (
                          <span className="text-red-400 text-xs truncate block max-w-[200px]" title={webhook.last_error}>
                            {webhook.last_error.substring(0, 50)}...
                          </span>
                        ) : (
                          <span className="text-zinc-600">-</span>
                        )}
                      </td>
                      <td className="py-3 px-2">
                        {(webhook.status === 'failed' || webhook.status === 'retrying') && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleRetry(webhook.event_id)}
                            disabled={retrying[webhook.event_id]}
                            className="border-primary text-primary hover:bg-primary hover:text-white"
                          >
                            {retrying[webhook.event_id] ? (
                              <RefreshCw className="h-3 w-3 animate-spin" />
                            ) : (
                              <RotateCcw className="h-3 w-3" />
                            )}
                            <span className="ml-1">Retry</span>
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Dead Letter Queue Highlight */}
      {stats.failed > 0 && (
        <Card className="bg-red-900/20 border-red-800">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <AlertTriangle className="h-6 w-6 text-red-500" />
              <div>
                <p className="text-red-400 font-medium">
                  {stats.failed} Webhook(s) permanent fehlgeschlagen
                </p>
                <p className="text-red-400/70 text-sm">
                  Diese Webhooks haben alle Retry-Versuche aufgebraucht. Bitte manuell prüfen.
                </p>
              </div>
              <Button 
                variant="outline" 
                className="ml-auto border-red-600 text-red-400"
                onClick={() => setFilter('failed')}
              >
                Anzeigen
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default WebhookAdmin;
